/**
 * Public Review API functions
 * These endpoints don't require authentication - they use unique review tokens per contact
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Types
export interface ReviewDeliverable {
  id: number;
  uuid: string;
  title: string;
  description: string;
  type: "blog" | "video" | "report";
  url?: string;
  status: "pending" | "approved" | "needs_revision";
  created_at: string;
  delivery_month?: number;
  delivery_year?: number;
}

export interface ReviewLink {
  status: "pending" | "approved" | "needs_revision";
  reviewed_at?: string;
  sent_at?: string;
  viewed_at?: string;
}

export interface ReviewContact {
  id: number;
  name: string;
  designation?: string;
}

export interface ReviewSchool {
  slate_id: number;
  slate_name: string;
  slate_slug?: string;
  city_name?: string;
  area_name?: string;
  logo?: string;
}

export interface BlogContent {
  title: string;
  content: string; // HTML content
  excerpt?: string;
  featuredImage?: string;
  author?: string;
  publishedAt?: string;
}

export interface ReviewComment {
  id: number;
  user_name: string;
  comment: string;
  created_at: string;
}

export interface ReviewPageData {
  deliverable: ReviewDeliverable;
  reviewLink: ReviewLink;
  contact: ReviewContact | null;
  school: ReviewSchool | null;
  blogContent: BlogContent | null;
  comments: ReviewComment[];
  alreadyReviewed: boolean;
}

/**
 * Fetch review page data by token
 * This is a public endpoint - no auth required
 * Token is unique per contact - contact info is returned from the server
 */
export const getReviewData = async (token: string): Promise<{
  success: boolean;
  data?: ReviewPageData;
  message?: string;
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/review/${token}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        message: data.message || 'Failed to load review page',
      };
    }

    return {
      success: true,
      data: data.data,
    };
  } catch (error: any) {
    console.error('Error fetching review data:', error);
    return {
      success: false,
      message: error.message || 'Failed to load review page',
    };
  }
};

/**
 * Approve a deliverable
 * This is a public endpoint - no auth required
 * Contact is identified by the unique token (no name needed)
 */
export const approveDeliverable = async (
  token: string
): Promise<{
  success: boolean;
  message?: string;
  data?: { status: string; reviewer_name: string; reviewed_at: string };
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/review/${token}/approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({}),
    });

    const data = await response.json();

    return {
      success: data.success,
      message: data.message,
      data: data.data,
    };
  } catch (error: any) {
    console.error('Error approving deliverable:', error);
    return {
      success: false,
      message: error.message || 'Failed to approve deliverable',
    };
  }
};

/**
 * Request revision for a deliverable
 * This is a public endpoint - no auth required
 * Contact is identified by the unique token (no name needed)
 */
export const requestRevision = async (
  token: string,
  comment: string
): Promise<{
  success: boolean;
  message?: string;
  data?: { status: string; reviewer_name: string; reviewed_at: string };
}> => {
  try {
    const response = await fetch(`${API_BASE_URL}/review/${token}/revision`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ comment }),
    });

    const data = await response.json();

    return {
      success: data.success,
      message: data.message,
      data: data.data,
    };
  } catch (error: any) {
    console.error('Error requesting revision:', error);
    return {
      success: false,
      message: error.message || 'Failed to submit revision request',
    };
  }
};
